import { CommonModule } from '@angular/common';
import { Component, inject, Inject } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { PagesService } from '@services/pages.service';
import { SharedService } from '@services/shared.service';
import { AuthService } from 'src/app/Auth/AuthService';
import { OnboardingDraftService } from '../onboarding-draft.service';
import { FormsModule, FormGroup, ReactiveFormsModule, Validators, FormControl } from '@angular/forms';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzModalModule } from 'ng-zorro-antd/modal';

@Component({
  selector: 'app-otpverification',
  standalone: true,
  imports: [FormsModule, RouterLink, CommonModule, NzSelectModule, NzInputModule, NzModalModule, ReactiveFormsModule],
  templateUrl: './otpverification.component.html',
  styleUrls: ['./otpverification.component.scss', '../../../../auth.scss'],
})
export class OtpverificationComponent {
  pagesService = inject(PagesService);


  firstChar: string = '';
  secondChar: string = '';
  thirdChar: string = '';
  route = inject(ActivatedRoute)
  fourthChar: string = '';
  codeSendChannel: string | null = '';
  optExpiray: string =
    'This code is valid for 10 minutes only. Please do not share it with anyone.';
  resendOpt: boolean = false;
  private otpInterval: any;
  enterprizeForm!: FormGroup;
  planData: any = null;



  ngOnInit() {
    this.enterprizeForm = new FormGroup({
      plan_id: new FormControl(null, Validators.required),
      name: new FormControl('', [Validators.required, Validators.minLength(3)]),
      email: new FormControl('', [Validators.required, Validators.email]),
      phone_number: new FormControl('', [Validators.required, Validators.pattern('^[0-9]{10,15}$')]),
      company_name: new FormControl('', Validators.required),
      address: new FormControl('', Validators.required),
      payment_method: new FormControl(null, Validators.required),
      details: new FormControl('', Validators.required),
    });


    this.codeSendChannel = localStorage.getItem('optmail');
    this.startOtpCountdown();
    // No token = no plan picked before signup (also how a resumed signup arrives from login).
    this.route.queryParams.subscribe(params => {
      const token = params['token'];
      try {
        this.planData = token ? JSON.parse(atob(token)) : null;
      } catch (err) {
        console.error('Invalid token format', err);
        this.planData = null;
      }
    });
  }

  ngOnDestroy() {
    if (this.otpInterval) {
      clearInterval(this.otpInterval);
    }
  }

  constructor(
    private authService: AuthService,
    private sharedService: SharedService,
    private router: Router,
    private pageService: PagesService,
    private draft: OnboardingDraftService,

  ) { }

  onInput(
    event: Event,
    previousInput: HTMLInputElement | null,
    currentInput: HTMLInputElement,
    nextInput?: HTMLInputElement
  ) {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    // Only allow one digit
    if (!/^\d$/.test(value)) {
      input.value = '';
      return;
    }

    // Auto move to next
    if (nextInput) {
      nextInput.focus();
    }
  }

  onKeyDown(
    event: KeyboardEvent,
    previousInput: HTMLInputElement | null,
    currentInput: HTMLInputElement
  ) {
    if (event.key === 'Backspace') {
      if (!currentInput.value && previousInput) {
        previousInput.focus();
      }
    }
  }

  onPaste(event: ClipboardEvent) {
    event.preventDefault();

    const pasteData = event.clipboardData?.getData('text') || '';
    const digits = pasteData.replace(/\D/g, '').split('');

    this.firstChar = digits[0] || '';
    this.secondChar = digits[1] || '';
    this.thirdChar = digits[2] || '';
    this.fourthChar = digits[3] || '';

    setTimeout(() => {
      if (this.fourthChar) {
        (document.querySelector('[name="fourthChar"]') as HTMLInputElement)?.focus();
      } else if (this.thirdChar) {
        (document.querySelector('[name="thirdChar"]') as HTMLInputElement)?.focus();
      } else if (this.secondChar) {
        (document.querySelector('[name="secondChar"]') as HTMLInputElement)?.focus();
      } else {
        (document.querySelector('[name="firstChar"]') as HTMLInputElement)?.focus();
      }
    }, 0);
  }
  enterprizeModal: boolean = false;
  verifyOtp() {
    this.sharedService.showLoader();

    const otp = `${this.firstChar}${this.secondChar}${this.thirdChar}${this.fourthChar}`;

    if (otp.length !== 4) {
      this.sharedService.createNotification('warning', 'Please enter the full OTP.');
      this.sharedService.hideLoader();
      return;
    }

    const expiryRaw = localStorage.getItem('otp_expiry');
    if (expiryRaw && new Date().getTime() > parseInt(expiryRaw)) {
      this.sharedService.createNotification('error', 'OTP has expired.');
      this.sharedService.hideLoader();
      return;
    }

    const email = localStorage.getItem('optmail');
    const optData = { otp, email };

    this.authService.login('VERIFY_OPT', optData).subscribe({
      next: (response: any) => {
        this.draft.clear();
        if (this.otpInterval) {
          clearInterval(this.otpInterval);
          this.otpInterval = null;
        }

        this.optExpiray = '00:00:00';
        this.resendOpt = true;

        this.sharedService.createNotification('success', 'Your OTP has been verified successfully');
        this.sharedService.hideLoader();

        if (this.planData?.isEnterprise) {
          this.enterprizeModal = true;
          this.enterprizeForm.get('plan_id')?.setValue(this.planData.id);
          return;
        } else if (this.planData?.monthly_price == 0) {
          this.activatePlan(this.planData.id);
        } else if (this.planData) {
          this.subscribAPlan(this.planData.id, this.planData.selected_mode.toLowerCase());
        }

        const rawForgot = localStorage.getItem('forgot');
        this.sharedService.closeAllModals();
        if (rawForgot) {
          localStorage.removeItem('forgot');
          this.router.navigate(['/auth/forgot']); // there is no token-less reset route
        } else {
          this.router.navigate(['/auth/registered-success']);
        }
      },
      error: (error: any) => {
        this.sharedService.hideLoader();

        if (error?.error?.code === 'ERROR_FATAL' && error?.error?.message === 'OTP invalid') {
          this.sharedService.createNotification('error', 'Invalid OTP');
          return;
        }

        const backendMessage =
          error?.error?.message || 'Something went wrong. Please try again.';
        this.sharedService.createNotification('error', backendMessage);

        // A code that is locked out or expired can never verify — the backend says "request a
        // new code", so offer that now instead of making them sit out the rest of the countdown.
        if (/too many|request a new code|expired|no longer valid/i.test(backendMessage)) {
          this.allowResendNow();
        }
      },
    });

  }


  private activatePlan(id: number) {
    const payload = {
      plan_id: id // Starter free plan ID
    };

    // Show loader
    this.sharedService.showLoader();

    this.pageService.create('FREE_PLAN', payload).subscribe({
      next: (res: any) => {
        // Hide loader
        this.sharedService.hideLoader();

        this.sharedService.createNotification(
          'success',
          'Free plan subscribed successfully'
        );
      },
      error: (err: any) => {
        // Hide loader
        this.sharedService.hideLoader();

        // The refusal is under err.error (see MicroApiService.call) — reading err.code directly
        // never matched, so every failure here was swallowed without a word.
        const message: string = err?.error?.message || '';
        if (/already.*free plan|free plan.*used/i.test(message)) {
          this.sharedService.createNotification('info', 'You have already used the free plan.');
        } else {
          this.sharedService.createNotification(
            'error',
            message || 'The free plan could not be activated. You can choose it again after signing in.'
          );
        }
      }
    });
  }
  subscribAPlan(plan_id: string, activeTabId: string) {
    const type = {
      plan_id,
      success_url: `${this.sharedService.getBaseUrl()}/auth/payment-approved?session_id={CHECKOUT_SESSION_ID}`,
      cancel_url: `${this.sharedService.getBaseUrl()}/auth/payment-rejected`,
      type: activeTabId,
    };

    this.sharedService.showLoader();

    this.authService.login('CHOOSE_SUBSCRIPTION_PLAN', type).subscribe({
      next: (Response: any) => {
        setTimeout(() => {
          this.sharedService.hideLoader();
          if (Response.checkout_url) {
            window.location.href = Response.checkout_url;
          } else {
            this.sharedService.createNotification(
              'error',
              'Checkout URL not found.'
            );
          }
        }, 2500);
      },
      error: (error: any) => {
        this.sharedService.hideLoader();
        this.sharedService.createNotification(
          'error',
          error?.error?.message || 'Failed to initiate subscription.'
        );
      },
    });
  }


  resendOtp() {
    // Clear any existing countdown
    if (this.otpInterval) {
      clearInterval(this.otpInterval);
    }

    this.sharedService.showLoader();
    this.pageService.update('SEND_OPT', null).subscribe({
      next: (response: any) => {
        localStorage.setItem('optmail', response.email);
        localStorage.setItem('otp_expiry', response.otp_expiry_timestamp);

        this.resendOpt = false;

        // ✅ Show backend message (result1)
        const backendMessage =
          response?.result1 || 'OTP sent successfully. Please check your inbox.';
        this.sharedService.createNotification('success', backendMessage);

        this.sharedService.hideLoader();

        // Restart countdown
        this.startOtpCountdown();
      },
      error: (err) => {
        this.sharedService.hideLoader();

        // ✅ Show backend error message if available
        const backendError =
          err?.error?.message || 'Failed to send OTP. Please try again.';
        this.sharedService.createNotification('error', backendError);
      },
    });
  }


  private allowResendNow() {
    if (this.otpInterval) {
      clearInterval(this.otpInterval);
      this.otpInterval = null;
    }
    this.optExpiray = '00:00:00';
    this.resendOpt = true;
  }

  startOtpCountdown() {
    const expiryTimestamp = localStorage.getItem('otp_expiry');
    if (expiryTimestamp) {
      const expiryDate = new Date(parseInt(expiryTimestamp));
      const updateCountdown = () => {
        const now = new Date();
        const timeDiff = expiryDate.getTime() - now.getTime();

        if (timeDiff > 0) {
          const hours = Math.floor(timeDiff / (1000 * 60 * 60));
          const minutes = Math.floor(
            (timeDiff % (1000 * 60 * 60)) / (1000 * 60)
          );
          const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);
          this.optExpiray = `${hours.toString().padStart(2, '0')}:${minutes
            .toString()
            .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        } else {
          this.optExpiray = '00:00:00';
          this.resendOpt = true;
          clearInterval(this.otpInterval); // stop when expired
        }
      };

      updateCountdown(); // initial update
      this.otpInterval = setInterval(updateCountdown, 1000); // ✅ assign interval
    }
  }

  updateTimer(expiryDate: Date) {
    const timeDiff = expiryDate.getTime() - new Date().getTime();
    const hours = Math.floor(timeDiff / (1000 * 60 * 60));
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

    this.optExpiray = `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }


  handleEnterprizeOk(): void {
    if (!this.enterprizeForm.valid) {
      this.enterprizeForm.markAllAsTouched();
      return;
    }

    const form = this.enterprizeForm;
    const basePayload = {

      plan_id: form.get('plan_id')?.value,
      name: form.get('name')?.value,
      email: form.get('email')?.value,
      phone_number: form.get('phone_number')?.value,
      company_name: form.get('company_name')?.value,
      address: form.get('address')?.value,
      payment_method: form.get('payment_method')?.value,
      details: form.get('details')?.value,
    };

    this.sharedService.showLoader();

    this.pagesService.update('ENTERPRISE_PLAN', basePayload).subscribe({
      next: () => {
        this.sharedService.createNotification(
          'success',
          'Your enterprise request has been submitted successfully. Please wait for admin approval.'
        );

        this.enterprizeForm.reset();
        this.enterprizeModal = false;

        this.sharedService.hideLoader();

        // ✅ Redirect user to login page
        this.router.navigate(['/auth/login']);
      },
      error: (err: any) => {
        this.sharedService.createNotification(
          'error',
          err?.error?.message || 'Unable to submit your request at this time. Please try again later.'
        );
        this.sharedService.hideLoader();
      },
    });
  }

  handleEnterprizeCancel(): void {
    this.enterprizeModal = false;
    this.enterprizeForm.reset();
    // The OTP is already verified at this point, so closing the Contact Us form must not leave
    // them on the Insert OTP page as if it were not (QA #283).
    this.router.navigate(['/auth/registered-success']);
  }
  goBack(): void {
    const token = this.createSecretToken(this.planData);
    this.router.navigate(['/auth/company'], { queryParams: { token } });
  }
    
  createSecretToken(plan: any): string {
    const payload = plan;
    return btoa(JSON.stringify(payload));
  }
}
